/**
 * CandidateInterviewSelector Component
 * 
 * Allows candidates to:
 * 1. Select from pre-defined public JDs
 * 2. Create a custom role for their interview
 * 
 * Redesigned for a simplistic, single-page master-detail view.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { createInterviewSlot, createCompanyJobDescription } from '../graphql/mutations';
import { listCompanyProfiles } from '../graphql/queries';
import { fetchPublicJDs, PUBLIC_COMPANY_CONFIG, seedPublicJDs } from '../utils/publicJDSeed';
import Header from '../components/header/Header';
import { RiseLoader } from 'react-spinners';
import { 
  FiSearch, 
  FiPlus, 
  FiBriefcase, 
  FiClock, 
  FiMapPin, 
  FiCheck, 
  FiChevronRight,
  FiCpu,
  FiLayers
} from 'react-icons/fi';

// Categories for filtering JDs
const CATEGORIES = [
  'All',
  'Engineering',
  'Data Science',
  'Product',
  'Design',
  'Business',
  'Support',
  'Security'
];

// Experience levels for custom role
const EXPERIENCE_LEVELS = [
  '0-1 years (Fresher)',
  '1-3 years',
  '2-5 years',
  '5+ years',
  '10+ years (Senior)'
];

const CandidateInterviewSelector = () => {
  const navigate = useNavigate();
  
  // State for JD selection
  const [publicJDs, setPublicJDs] = useState([]);
  const [filteredJDs, setFilteredJDs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJD, setSelectedJD] = useState(null);
  
  // State for custom role
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customRole, setCustomRole] = useState({
    jobTitle: '',
    skills: '',
    experience: '1-3 years',
    description: ''
  });
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [seedingJDs, setSeedingJDs] = useState(false);
  
  // Candidate info from localStorage
  const candidateProfileID = localStorage.getItem('candidateProfileId');
  const candidateEmail = localStorage.getItem('candidateProfileEmailID');
  const candidateProfile = JSON.parse(localStorage.getItem('candidateProfile') || '{}');

  // Fetch public JDs on mount
  useEffect(() => {
    loadPublicJDs();
  }, []);

  // Filter JDs when category or search changes
  useEffect(() => {
    let filtered = publicJDs;
    
    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(jd => jd.selectedCategory === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(jd => 
        jd.jobTitle.toLowerCase().includes(query) ||
        jd.skill?.toLowerCase().includes(query) ||
        jd.jobDescription?.toLowerCase().includes(query)
      );
    }
    
    setFilteredJDs(filtered);
  }, [publicJDs, selectedCategory, searchQuery]);

  const loadPublicJDs = async () => {
    try {
      setLoading(true);
      const jds = await fetchPublicJDs();
      
      if (jds.length === 0) {
        console.log('No public JDs found. Seeding...');
        setSeedingJDs(true);
        await seedPublicJDs();
        const seededJDs = await fetchPublicJDs();
        setPublicJDs(seededJDs);
        setSeedingJDs(false);
      } else {
        setPublicJDs(jds);
      }
    } catch (err) {
      console.error('Error loading public JDs:', err);
      setError('Failed to load job descriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJDSelect = (jd) => {
    // Toggle selection - deselect if already selected
    if (selectedJD?.id === jd.id) {
      setSelectedJD(null);
    } else {
      setSelectedJD(jd);
      setShowCustomForm(false);
    }
  };

  const handleCustomRoleClick = () => {
    setShowCustomForm(true);
    setSelectedJD(null);
  };

  const handleStartInterview = async () => {
    if (!candidateProfileID || !candidateEmail) {
      setError('Please complete your profile registration first.');
      setTimeout(() => navigate('/candidateProfileReg'), 2000);
      return;
    }

    setCreating(true);
    setError(null);

    try {
      let jdID = null;
      let jobTitle = '';
      let companyID = null;
      let companyName = 'GetScreened Public Jobs';

      // Get public company ID
      const companyRes = await API.graphql(graphqlOperation(listCompanyProfiles, {
        filter: { email: { eq: PUBLIC_COMPANY_CONFIG.email } }
      }));

      if (companyRes.data.listCompanyProfiles.items.length > 0) {
        companyID = companyRes.data.listCompanyProfiles.items[0].id;
        companyName = companyRes.data.listCompanyProfiles.items[0].companyName;
      }

      if (selectedJD) {
        // Use selected public JD
        jdID = selectedJD.id;
        jobTitle = selectedJD.jobTitle;
        companyID = selectedJD.companyProfileID || companyID;
      } else if (showCustomForm && customRole.jobTitle.trim()) {
        // Create custom JD for this interview
        const customJDInput = {
          companyProfileID: companyID,
          jobIDNo: Date.now(), // Unique ID
          jobTitle: customRole.jobTitle.trim(),
          selectedCategory: 'Custom',
          selectedExperience: customRole.experience,
          skill: customRole.skills.trim() || 'General Skills',
          jobDescription: customRole.description.trim() || `Interview for ${customRole.jobTitle} position.`,
          compensation: 'Not Disclosed',
          benefits: 'Standard Benefits',
          workMode: 'Not Specified'
        };

        const jdRes = await API.graphql(graphqlOperation(createCompanyJobDescription, {
          input: customJDInput
        }));

        jdID = jdRes.data.createCompanyJobDescription.id;
        jobTitle = customRole.jobTitle.trim();
      } else {
        setError('Please select a role or create a custom one.');
        setCreating(false);
        return;
      }

      // Create meeting config with candidate-initiated flag
      const meetingConfigObj = {
        companyID: companyID,
        companyName: companyName,
        jobTitle: jobTitle,
        jdID: jdID,
        interviewType: 'candidate-initiated', // Flag for candidate-initiated interview
        candidateName: candidateProfile.fullName || 'Candidate',
        createdAt: new Date().toISOString(),
        // Candidate-specific flags
        isCandidateInitiated: true,
        candidateProfileID: candidateProfileID,
        numberOfQuestions: 5, // Default 5 questions for candidate interviews
        // Custom role details if applicable
        customRole: showCustomForm ? {
          jobTitle: customRole.jobTitle.trim(),
          skills: customRole.skills.trim(),
          experience: customRole.experience,
          description: customRole.description.trim()
        } : null
      };

      // Create interview slot
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
      
      const interviewSlotInput = {
        candidateProfileID: candidateProfileID,
        candidateEmail: candidateEmail,
        date: now.toISOString().split('T')[0], // AWSDate: YYYY-MM-DD
        startTime: now.toTimeString().split(' ')[0], // AWSTime: HH:MM:SS
        endTime: endTime.toTimeString().split(' ')[0], // AWSTime: HH:MM:SS
        status: 'ACTIVE',
        meetingLinkURL: '',
        meetingConfig: JSON.stringify(meetingConfigObj),
        candidateAttendance: false,
        botAttendance: false,
        jdID: jdID,
        candidateProfileInterviewSlotsId: candidateProfileID
      };

      const slotRes = await API.graphql(graphqlOperation(createInterviewSlot, {
        input: interviewSlotInput
      }));

      const slotId = slotRes.data.createInterviewSlot.id;
      console.log('✅ Created interview slot:', slotId);

      // Navigate to public interview page
      navigate(`/public-interview/${slotId}`);

    } catch (err) {
      console.error('Error creating interview:', err);
      setError('Failed to create interview. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading || seedingJDs) {
    return (
      <div className="h-screen bg-slate-900 flex flex-col overflow-hidden">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center">
          <RiseLoader color="#3b82f6" loading={true} size={20} />
          <p className="text-slate-400 mt-4">
            {seedingJDs ? 'Setting up job descriptions...' : 'Loading available roles...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-900 text-slate-200 font-sans flex flex-col overflow-hidden">
      <Header />
      
      <div className="flex-1 flex overflow-hidden max-w-[1600px] mx-auto w-full">
        
        {/* LEFT PANEL: List & Filters (30% width) */}
        <div className="w-[30%] min-w-[300px] border-r border-slate-800 flex flex-col bg-slate-900/50">
          
          {/* Search & Header */}
          <div className="p-4 border-b border-slate-800">
            <h1 className="text-lg font-semibold text-white mb-3">Select Role</h1>
            
            <div className="relative mb-3">
              <FiSearch className="absolute left-3 top-3 text-slate-500" />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>

            {/* Custom Role Button */}
            <button
              onClick={handleCustomRoleClick}
              className={`w-full py-2 rounded-lg border border-dashed flex items-center justify-center gap-2 transition-all text-sm ${
                showCustomForm 
                  ? 'bg-blue-500/10 border-blue-500 text-blue-400' 
                  : 'border-slate-700 text-slate-400 hover:border-blue-500 hover:text-blue-400'
              }`}
            >
              <FiPlus className="text-lg" />
              <span className="font-medium">Create Custom Role</span>
            </button>
          </div>

          {/* Categories */}
          <div className="px-4 py-2 border-b border-slate-800 overflow-x-auto flex gap-2 no-scrollbar">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
            {filteredJDs.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                <p>No roles found.</p>
              </div>
            ) : (
              filteredJDs.map(jd => (
                <div
                  key={jd.id}
                  onClick={() => handleJDSelect(jd)}
                  className={`p-3 rounded-lg cursor-pointer transition-all border ${
                    selectedJD?.id === jd.id
                      ? 'bg-blue-600/10 border-blue-500/50 shadow-lg shadow-blue-500/10'
                      : 'bg-slate-800/50 border-slate-800 hover:bg-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <h3 className={`text-sm font-medium ${selectedJD?.id === jd.id ? 'text-blue-400' : 'text-white'}`}>
                      {jd.jobTitle}
                    </h3>
                    {selectedJD?.id === jd.id && <FiCheck className="text-blue-500" />}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <FiBriefcase /> {jd.selectedExperience}
                    </span>
                    <span className="flex items-center gap-1">
                      <FiLayers /> {jd.selectedCategory}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Details & Action (65% width) */}
        <div className="flex-1 bg-slate-900 flex flex-col relative">
          {error && (
            <div className="absolute top-4 left-4 right-4 z-50 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl text-sm text-center">
              {error}
            </div>
          )}

          {showCustomForm ? (
            // CUSTOM FORM VIEW
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar flex flex-col justify-center max-w-2xl mx-auto w-full">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-1">Define Custom Role</h2>
                <p className="text-slate-400 text-sm">Tailor the interview to your specific skills and experience.</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-xs font-medium mb-1.5">Job Title</label>
                  <input
                    type="text"
                    placeholder="e.g. Senior Frontend Engineer"
                    value={customRole.jobTitle}
                    onChange={(e) => setCustomRole({ ...customRole, jobTitle: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs font-medium mb-1.5">Experience Level</label>
                    <select
                      value={customRole.experience}
                      onChange={(e) => setCustomRole({ ...customRole, experience: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors appearance-none"
                    >
                      {EXPERIENCE_LEVELS.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs font-medium mb-1.5">Key Skills</label>
                    <input
                      type="text"
                      placeholder="e.g. React, Node.js, AWS"
                      value={customRole.skills}
                      onChange={(e) => setCustomRole({ ...customRole, skills: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-400 text-xs font-medium mb-1.5">Role Description (Optional)</label>
                  <textarea
                    rows={3}
                    placeholder="Paste a job description or type specific requirements..."
                    value={customRole.description}
                    onChange={(e) => setCustomRole({ ...customRole, description: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors resize-none"
                  />
                </div>

                <div className="pt-2">
                  <button
                    onClick={handleStartInterview}
                    disabled={creating || !customRole.jobTitle.trim()}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      customRole.jobTitle.trim()
                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {creating ? 'Creating Interview...' : 'Start Custom Interview'}
                    {!creating && <FiChevronRight />}
                  </button>
                </div>
              </div>
            </div>
          ) : selectedJD ? (
            // SELECTED JD VIEW
            <div className="flex-1 flex flex-col h-full">
              {/* Header */}
              <div className="p-6 border-b border-slate-800 bg-slate-900/50">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{selectedJD.jobTitle}</h2>
                    <div className="flex items-center gap-3 text-slate-400">
                      <span className="flex items-center gap-1.5 bg-slate-800 px-2.5 py-0.5 rounded-full text-xs">
                        <FiBriefcase className="text-blue-400" /> {selectedJD.selectedExperience}
                      </span>
                      <span className="flex items-center gap-1.5 bg-slate-800 px-2.5 py-0.5 rounded-full text-xs">
                        <FiMapPin className="text-emerald-400" /> {selectedJD.workMode}
                      </span>
                      <span className="flex items-center gap-1.5 bg-slate-800 px-2.5 py-0.5 rounded-full text-xs">
                        <FiLayers className="text-purple-400" /> {selectedJD.selectedCategory}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <FiCpu /> Required Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedJD.skill.split(',').map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-md text-xs">
                          {skill.trim()}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Role Description</h3>
                    <div className="prose prose-invert max-w-none text-slate-300 leading-relaxed text-sm">
                      <p>{selectedJD.jobDescription}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Action */}
              <div className="p-6 border-t border-slate-800 bg-slate-900/50">
                <button
                  onClick={handleStartInterview}
                  disabled={creating}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold text-sm shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-2"
                >
                  {creating ? 'Setting up Interview...' : 'Start Interview'}
                  {!creating && <FiChevronRight />}
                </button>
              </div>
            </div>
          ) : (
            // EMPTY STATE
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 p-8 text-center">
              <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-6">
                <FiSearch className="text-4xl text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Select a Role</h3>
              <p className="max-w-md mx-auto">
                Choose a role from the list on the left to view details, or create a custom role to match your specific needs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateInterviewSelector;
